import API from "@irati/utils/services/API"
import client, {WatchQueryOptions} from "@irati/utils/services/apollo"
import graph from "@irati/utils/services/graph"
import {LoginActiveSession, User, UnauthedBusiness, Business} from "@irati/types"
import PBService from "@irati/utils/services"
import {isGlobalAdmin} from "@irati/identity/helpers/user/role"
import authentication from "@irati/auth"
import loginErrorCodes from "../errorCodes/Login"
import AuthStream from "../streams/auth"
import loginErrorHandler from "./errors/legacy/loginErrorHandler"
import {toString as loginQuery} from "./queries/login"
import {toString as ssoLoginQuery} from "./queries/ssoLogin"
import activeSessionsQuery from "./queries/loginActiveSessions"
import loginRemoveActiveSessionMutation from "./mutations/loginRemoveActiveSession"
import loginLogoutMutation from "./mutations/loginLogout"
import tokens from "./tokens"
import mfaTotpService from "./mfa_totp"
import mfaService from "./mfa"
import authErrorHandler from "./errors/authErrorHandler"

interface ActiveSessions {
  loginActiveSessions: LoginActiveSession[]
}

class Login extends PBService {
  watchActive(userId = authentication.user().uuid, options: WatchQueryOptions = {}) {
    const variables = {userId}
    return client.watchQuery<ActiveSessions>({...options, query: activeSessionsQuery, variables})
  }

  removeActiveSession(sessionId: string, userId = authentication.user().uuid): Promise<boolean> {
    const variables = {userId, sessionId}

    return client.mutate({mutation: loginRemoveActiveSessionMutation, variables}).then(({data}) => {
      const cachedData = client.readQuery<ActiveSessions>({query: activeSessionsQuery, variables: {userId}})
      if (cachedData?.loginActiveSessions) {
        client.writeQuery({
          query: activeSessionsQuery,
          variables: {userId},
          data: {
            loginActiveSessions: cachedData.loginActiveSessions.filter((t) => t.uuid !== sessionId)
          }
        })
      }
      return data.loginRemoveActiveSession
    })
  }

  logout({redirectOnLogout = true, logOutMutation = true} = {}): Promise<boolean> {
    if (API.hasTokens() && logOutMutation) {
      client.mutate({mutation: loginLogoutMutation})
    }
    const original = AuthStream.shouldRedirectOnLogout
    AuthStream.shouldRedirectOnLogout = redirectOnLogout
    return AuthStream.stop().then(() => {
      AuthStream.shouldRedirectOnLogout = original
      return true
    })
  }

  login(tenant: UnauthedBusiness, email: string, password: string): Promise<void> {
    const options = {
      useErrorHandler: false,
      unAuth: true
    }
    API.sentryIgnoreError(options, [loginErrorCodes.LoginChallengeNewPasswordRequired, loginErrorCodes.UserNotFound])
    const variables = {
      email,
      password,
      tenantId: tenant.uuid
    }
    return graph({query: loginQuery, variables, operationName: "Login"}, options).then((data) => {
      const {accessToken, ...rest} = data.login
      tokens.set({accessToken})
      return forceAdminMFASetup(rest.business, rest.user).then(() => {
        return AuthStream.start(rest)
      })
    })
  }

  ssoLogin(accessToken: string): Promise<void> {
    const options = {
      useErrorHandler: false
    }
    tokens.set({accessToken})
    return graph({query: ssoLoginQuery, operationName: "SSOLogin"}, options).then((data) => {
      return forceAdminMFASetup(data.login.business, data.login.user).then(() => {
        return AuthStream.start(data.login)
      })
    })
  }

  oneTimePasswordLogin(accessToken: string): Promise<void> {
    tokens.set({accessToken})
    return graph({query: ssoLoginQuery, operationName: "SSOLogin"}).then((data) => {
      return forceAdminMFASetup(data.login.business, data.login.user).then(() => {
        return AuthStream.start(data.login)
      })
    })
  }
}

const loginService = new Login({errorHandlers: [loginErrorHandler, authErrorHandler]})

export default loginService

async function forceAdminMFASetup(business: Business, user: User) {
  if (isGlobalAdmin(business, user)) {
    const mfaSetup = await mfaService.getUser(user.uuid!)
    const mfaTotpSetup = await mfaTotpService.getUser(user.uuid!)
    const hasSetupSMS = mfaSetup && mfaSetup.isConfirmed
    const hasSetupTotp = mfaTotpSetup && mfaTotpSetup.isConfirmed
    if (!hasSetupSMS && !hasSetupTotp) {
      try {
        await mfaService.hasFlag()
      } catch (e) {
        const err = new Error() as PBError
        err.message = "platform.mfa.mfaError.adminsRequired"
        throw err
      }
    }
  }
}
